import { useState, useEffect, useCallback } from 'react';
import API from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Switch } from '../components/ui/switch';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Plus, Trash, PencilSimple, Lightning, UserPlus, ArrowsLeftRight, UserMinus, ShieldCheck, Prohibit, Ticket, MagnifyingGlass, Check, CaretUpDown, User, Play, Clock, CheckCircle, XCircle, Gear } from '@phosphor-icons/react';
import { toast } from 'sonner';
import InfoTooltip from '../components/InfoTooltip';
import EmptyState from '../components/EmptyState';

export default function LifecyclePage() {
  const [tab, setTab] = useState('rules');
  const [rules, setRules] = useState([]);
  const [events, setEvents] = useState([]);
  const [stats, setStats] = useState({});
  const [roles, setRoles] = useState([]);
  const [identities, setIdentities] = useState([]);

  // Dialog states
  const [showRuleDialog, setShowRuleDialog] = useState(false);
  const [showTriggerDialog, setShowTriggerDialog] = useState(false);
  const [editingRule, setEditingRule] = useState(null);

  // Form states
  const [ruleForm, setRuleForm] = useState({
    name: '', description: '', event_type: 'joiner', trigger_field: 'department', trigger_value: '', actions: [], enabled: true
  });
  const [triggerForm, setTriggerForm] = useState({
    identity_id: '', event_type: 'joiner', details: {}
  });
  const [identitySearch, setIdentitySearch] = useState('');
  const [identityPopoverOpen, setIdentityPopoverOpen] = useState(false);
  const [triggerFieldValues, setTriggerFieldValues] = useState([]);

  // Filters
  const [eventFilter, setEventFilter] = useState('');
  const [ruleFilter, setRuleFilter] = useState('');

  const loadRules = useCallback(async () => {
    try {
      const res = await API.get('/lifecycle/rules', { params: { event_type: ruleFilter || undefined } });
      setRules(res.data.items);
    } catch { toast.error('Failed to load rules'); }
  }, [ruleFilter]);

  const loadEvents = useCallback(async () => {
    try {
      const res = await API.get('/lifecycle/events', { params: { event_type: eventFilter || undefined, limit: 50 } });
      setEvents(res.data.items);
    } catch { toast.error('Failed to load events'); }
  }, [eventFilter]);

  const loadStats = async () => {
    try {
      const res = await API.get('/lifecycle/stats');
      setStats(res.data);
    } catch {}
  };

  useEffect(() => { loadRules(); }, [loadRules]);
  useEffect(() => { loadEvents(); }, [loadEvents]);
  useEffect(() => { loadStats(); }, []);
  useEffect(() => {
    API.get('/roles', { params: { limit: 200 } }).then(r => setRoles(r.data.items)).catch(() => {});
    API.get('/identities', { params: { limit: 500, orphaned: false } }).then(r => setIdentities(r.data.items)).catch(() => {});
  }, []);

  // Derive distinct values for the selected trigger field from identities
  useEffect(() => {
    const field = ruleForm.trigger_field;
    if (!field || identities.length === 0) { setTriggerFieldValues([]); return; }
    const values = [...new Set(identities.map(i => i[field]).filter(v => v && v.trim()))].sort();
    setTriggerFieldValues(values);
  }, [ruleForm.trigger_field, identities]);

  // ========== Rule CRUD ==========
  const openNewRule = () => {
    setEditingRule(null);
    setRuleForm({ name: '', description: '', event_type: 'joiner', trigger_field: 'department', trigger_value: '', actions: [], enabled: true });
    setShowRuleDialog(true);
  };

  const openEditRule = (rule) => {
    setEditingRule(rule);
    setRuleForm({
      name: rule.name, description: rule.description || '', event_type: rule.event_type,
      trigger_field: rule.trigger_field || 'department', trigger_value: rule.trigger_value || '',
      actions: rule.actions || [], enabled: rule.enabled
    });
    setShowRuleDialog(true);
  };

  const handleSaveRule = async () => {
    if (!ruleForm.name.trim()) { toast.error('Rule name is required'); return; }
    try {
      if (editingRule) {
        await API.put(`/lifecycle/rules/${editingRule.id}`, ruleForm);
        toast.success('Rule updated');
      } else {
        await API.post('/lifecycle/rules', ruleForm);
        toast.success('Rule created');
      }
      setShowRuleDialog(false);
      loadRules();
      loadStats();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to save rule'); }
  };

  const handleDeleteRule = async (ruleId) => {
    if (!window.confirm('Delete this automation rule?')) return;
    try {
      await API.delete(`/lifecycle/rules/${ruleId}`);
      toast.success('Rule deleted');
      loadRules();
      loadStats();
    } catch { toast.error('Failed to delete'); }
  };

  const handleToggleRule = async (rule) => {
    try {
      await API.put(`/lifecycle/rules/${rule.id}`, { enabled: !rule.enabled });
      loadRules();
    } catch { toast.error('Failed to toggle'); }
  };

  // ========== Action management ==========
  const addAction = (type) => {
    const action = { type };
    if (type === 'assign_role' || type === 'revoke_role') {
      action.role_id = '';
      action.role_name = '';
    }
    if (type === 'create_ticket') {
      action.ticket_description = '';
      action.priority = 'medium';
    }
    setRuleForm({ ...ruleForm, actions: [...ruleForm.actions, action] });
  };

  const removeAction = (idx) => {
    setRuleForm({ ...ruleForm, actions: ruleForm.actions.filter((_, i) => i !== idx) });
  };

  const updateAction = (idx, field, value) => {
    const updated = [...ruleForm.actions];
    updated[idx] = { ...updated[idx], [field]: value };
    if (field === 'role_id') {
      const role = roles.find(r => r.id === value);
      updated[idx].role_name = role?.name || '';
    }
    setRuleForm({ ...ruleForm, actions: updated });
  };

  // ========== Trigger ==========
  const handleTrigger = async () => {
    if (!triggerForm.identity_id) { toast.error('Select an identity'); return; }
    try {
      const res = await API.post('/lifecycle/events/trigger', triggerForm);
      toast.success(`Event processed: ${res.data.actions_taken?.length || 0} action(s) executed`);
      setShowTriggerDialog(false);
      loadEvents();
      loadStats();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to trigger event'); }
  };

  const eventTypeIcon = (type) => {
    if (type === 'joiner') return <UserPlus size={16} className="text-emerald-500" />;
    if (type === 'mover') return <ArrowsLeftRight size={16} className="text-blue-500" />;
    if (type === 'leaver') return <UserMinus size={16} className="text-red-500" />;
    return <Lightning size={16} className="text-navy-400" />;
  };

  const eventTypeColor = (type) => {
    if (type === 'joiner') return 'bg-emerald-100 text-emerald-700';
    if (type === 'mover') return 'bg-blue-100 text-blue-700';
    if (type === 'leaver') return 'bg-red-100 text-red-700';
    return 'bg-slate-100 text-navy-600';
  };

  const actionTypeLabel = (type) => {
    switch (type) {
      case 'assign_role': return 'Assign Role';
      case 'revoke_role': return 'Revoke Role';
      case 'revoke_all_roles': return 'Revoke All Roles';
      case 'disable_identity': return 'Disable Identity';
      case 'create_ticket': return 'Create Ticket';
      default: return type;
    }
  };

  return (
    <div className="p-4 sm:p-6 md:p-8 space-y-6" data-testid="lifecycle-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-navy-900" style={{ fontFamily: 'Chivo, sans-serif' }}>Lifecycle Automations <InfoTooltip concept="lifecycle" /></h1>
          <p className="text-sm text-navy-500 mt-1">Configure Joiner/Mover/Leaver rules and trigger lifecycle events</p>
        </div>
        <div className="flex gap-2">
          <Button onClick={() => { setTriggerForm({ identity_id: '', event_type: 'joiner', details: {} }); setShowTriggerDialog(true); }} variant="outline" className="rounded-sm text-xs" data-testid="trigger-event-btn">
            <Play size={14} className="mr-1.5" /> Trigger Event
          </Button>
          <Button onClick={openNewRule} className="bg-orange-500 hover:bg-orange-600 text-white rounded-sm text-xs" data-testid="create-rule-btn">
            <Plus size={14} className="mr-1.5" /> New Rule
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
        <div className="bg-white border border-slate-200 rounded-sm p-4 text-center">
          <Gear size={18} className="mx-auto text-navy-800 mb-1" />
          <p className="text-2xl font-bold text-navy-900">{stats.total_rules || 0}</p>
          <p className="text-[10px] text-navy-500 uppercase tracking-wider">Rules</p>
        </div>
        <div className="bg-white border border-slate-200 rounded-sm p-4 text-center">
          <CheckCircle size={18} className="mx-auto text-emerald-700 mb-1" />
          <p className="text-2xl font-bold text-navy-900">{stats.active_rules || 0}</p>
          <p className="text-[10px] text-navy-500 uppercase tracking-wider">Active</p>
        </div>
        <div className="bg-white border border-slate-200 rounded-sm p-4 text-center">
          <Lightning size={18} className="mx-auto text-amber-700 mb-1" />
          <p className="text-2xl font-bold text-navy-900">{stats.total_events || 0}</p>
          <p className="text-[10px] text-navy-500 uppercase tracking-wider">Events</p>
        </div>
        <div className="bg-white border border-emerald-200 rounded-sm p-4 text-center">
          <UserPlus size={18} className="mx-auto text-emerald-700 mb-1" />
          <p className="text-2xl font-bold text-emerald-700">{stats.joiner_events || 0}</p>
          <p className="text-[10px] text-navy-500 uppercase tracking-wider">Joiners</p>
        </div>
        <div className="bg-white border border-blue-200 rounded-sm p-4 text-center">
          <ArrowsLeftRight size={18} className="mx-auto text-blue-700 mb-1" />
          <p className="text-2xl font-bold text-blue-700">{stats.mover_events || 0}</p>
          <p className="text-[10px] text-navy-500 uppercase tracking-wider">Movers</p>
        </div>
        <div className="bg-white border border-red-200 rounded-sm p-4 text-center">
          <UserMinus size={18} className="mx-auto text-red-700 mb-1" />
          <p className="text-2xl font-bold text-red-700">{stats.leaver_events || 0}</p>
          <p className="text-[10px] text-navy-500 uppercase tracking-wider">Leavers</p>
        </div>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="bg-slate-100 rounded-sm p-0.5">
          <TabsTrigger value="rules" className="rounded-sm text-xs data-[state=active]:bg-white data-[state=active]:shadow-sm px-4 py-2" data-testid="tab-rules">
            <Gear size={14} className="mr-1.5" /> Automation Rules
          </TabsTrigger>
          <TabsTrigger value="events" className="rounded-sm text-xs data-[state=active]:bg-white data-[state=active]:shadow-sm px-4 py-2" data-testid="tab-events">
            <Lightning size={14} className="mr-1.5" /> Event History
          </TabsTrigger>
        </TabsList>

        {/* RULES TAB */}
        <TabsContent value="rules" className="mt-4 space-y-3">
          <div className="flex items-center gap-2">
            {['', 'joiner', 'mover', 'leaver'].map(f => (
              <Button key={f} variant={ruleFilter === f ? 'default' : 'outline'} size="sm" onClick={() => setRuleFilter(f)}
                className={`rounded-sm text-xs ${ruleFilter === f ? 'bg-orange-500 hover:bg-orange-600 text-white' : ''}`}>
                {f ? f.charAt(0).toUpperCase() + f.slice(1) : 'All'}
              </Button>
            ))}
          </div>

          {rules.length === 0 ? (
            <EmptyState type="lifecycle" onAction={() => setShowRuleDialog(true)} />
          ) : (
            <div className="space-y-2">
              {rules.map(rule => (
                <div key={rule.id} className="bg-white border border-slate-200 rounded-sm shadow-sm px-5 py-4 flex items-center gap-4 hover:shadow-md transition-shadow" data-testid={`rule-card-${rule.id}`}>
                  <div className="w-10 h-10 rounded-sm shrink-0">
                    {eventTypeIcon(rule.event_type)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="text-sm font-bold text-navy-900">{rule.name}</p>
                      <Badge className={`text-[10px] ${eventTypeColor(rule.event_type)}`}>{rule.event_type}</Badge>
                      {!rule.enabled && <Badge className="text-[10px] bg-slate-100 text-navy-500">Disabled</Badge>}
                    </div>
                    <p className="text-xs text-navy-500 mt-0.5">{rule.description || 'No description'}</p>
                    <div className="flex items-center gap-3 mt-1">
                      {rule.trigger_value && <span className="text-[10px] text-navy-400">Trigger: {rule.trigger_field} = "{rule.trigger_value}"</span>}
                      <span className="text-[10px] text-navy-400">{(rule.actions || []).length} action(s)</span>
                      <span className="text-[10px] text-navy-400">Executed {rule.execution_count || 0}x</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <Switch checked={rule.enabled} onCheckedChange={() => handleToggleRule(rule)} data-testid={`toggle-rule-${rule.id}`} />
                    <Button variant="ghost" size="sm" onClick={() => openEditRule(rule)} className="h-8 w-8 p-0">
                      <PencilSimple size={14} />
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => handleDeleteRule(rule.id)} className="h-8 w-8 p-0 text-red-500 hover:text-red-700">
                      <Trash size={14} />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        {/* EVENTS TAB */}
        <TabsContent value="events" className="mt-4 space-y-3">
          <div className="flex items-center gap-2">
            {['', 'joiner', 'mover', 'leaver'].map(f => (
              <Button key={f} variant={eventFilter === f ? 'default' : 'outline'} size="sm" onClick={() => setEventFilter(f)}
                className={`rounded-sm text-xs ${eventFilter === f ? 'bg-orange-500 hover:bg-orange-600 text-white' : ''}`}>
                {f ? f.charAt(0).toUpperCase() + f.slice(1) : 'All'}
              </Button>
            ))}
          </div>

          <div className="bg-white border border-slate-200 rounded-sm shadow-sm overflow-hidden">
        <div className="h-1.5 bg-navy-600" />
            <Table wrapperClassName="max-h-[calc(100vh-420px)]">
              <TableHeader>
                <TableRow className="bg-slate-50 shadow-[0_1px_3px_rgba(0,0,0,0.05)]">
                  <TableHead className="text-[10px] font-bold uppercase tracking-wider text-navy-500">Type</TableHead>
                  <TableHead className="text-[10px] font-bold uppercase tracking-wider text-navy-500">Identity</TableHead>
                  <TableHead className="text-[10px] font-bold uppercase tracking-wider text-navy-500">Actions Taken</TableHead>
                  <TableHead className="text-[10px] font-bold uppercase tracking-wider text-navy-500">Triggered By</TableHead>
                  <TableHead className="text-[10px] font-bold uppercase tracking-wider text-navy-500">Date</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {events.length === 0 ? (
                  <TableRow><TableCell colSpan={5}><EmptyState type="lifecycle_events" /></TableCell></TableRow>
                ) : events.map(event => (
                  <TableRow key={event.id} className="hover:bg-slate-50">
                    <TableCell>
                      <div className="flex items-center gap-2">
                        {eventTypeIcon(event.event_type)}
                        <Badge className={`text-[10px] ${eventTypeColor(event.event_type)}`}>{event.event_type}</Badge>
                      </div>
                    </TableCell>
                    <TableCell>
                      <p className="text-sm font-medium text-navy-900">{event.identity_name}</p>
                      <p className="text-xs text-navy-400">{event.identity_email}</p>
                    </TableCell>
                    <TableCell>
                      <div className="space-y-0.5">
                        {(event.actions_taken || []).map((a, i) => (
                          <div key={`${event.id}-action-${a.action_type || i}`} className="flex items-center gap-1">
                            {a.status === 'completed' ? <CheckCircle size={10} className="text-emerald-500" /> : <XCircle size={10} className="text-navy-400" />}
                            <span className="text-xs text-navy-600">{a.details || a.action_type}</span>
                          </div>
                        ))}
                        {(event.actions_taken || []).length === 0 && <span className="text-xs text-navy-400">No matching rules</span>}
                      </div>
                    </TableCell>
                    <TableCell className="text-sm text-navy-600">{event.triggered_by}</TableCell>
                    <TableCell className="text-sm text-navy-500">{new Date(event.created_at).toLocaleString()}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </TabsContent>
      </Tabs>

      {/* CREATE / EDIT RULE DIALOG */}
      <Dialog open={showRuleDialog} onOpenChange={setShowRuleDialog}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Chivo, sans-serif' }}>
              {editingRule ? 'Edit Automation Rule' : 'Create Automation Rule'}
            </DialogTitle>
            <DialogDescription>
              Configure when and what should happen during identity lifecycle changes.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider">Rule Name *</Label>
              <Input value={ruleForm.name} onChange={(e) => setRuleForm({...ruleForm, name: e.target.value})} className="mt-1 rounded-sm" placeholder="e.g., Engineering Joiner Setup" data-testid="rule-name-input" />
            </div>
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider">Description</Label>
              <Input value={ruleForm.description} onChange={(e) => setRuleForm({...ruleForm, description: e.target.value})} className="mt-1 rounded-sm" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs font-semibold uppercase tracking-wider">Event Type *</Label>
                <Select value={ruleForm.event_type} onValueChange={(v) => setRuleForm({...ruleForm, event_type: v})}>
                  <SelectTrigger className="mt-1 rounded-sm"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="joiner"><div className="flex items-center gap-2"><UserPlus size={14} className="text-emerald-500" /> Joiner</div></SelectItem>
                    <SelectItem value="mover"><div className="flex items-center gap-2"><ArrowsLeftRight size={14} className="text-blue-500" /> Mover</div></SelectItem>
                    <SelectItem value="leaver"><div className="flex items-center gap-2"><UserMinus size={14} className="text-red-500" /> Leaver</div></SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs font-semibold uppercase tracking-wider">Trigger Field</Label>
                <Select value={ruleForm.trigger_field} onValueChange={(v) => setRuleForm({...ruleForm, trigger_field: v})}>
                  <SelectTrigger className="mt-1 rounded-sm"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="department">Department</SelectItem>
                    <SelectItem value="job_title">Job Title</SelectItem>
                    <SelectItem value="lifecycle_state">Lifecycle State</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider">Trigger Value (leave empty to match all)</Label>
              <Select value={ruleForm.trigger_value || '__all__'} onValueChange={(v) => setRuleForm({...ruleForm, trigger_value: v === '__all__' ? '' : v})}>
                <SelectTrigger className="mt-1 rounded-sm"><SelectValue placeholder="Match all" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all__">Match all (any value)</SelectItem>
                  {triggerFieldValues.map(val => (
                    <SelectItem key={val} value={val}>{val}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Actions */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label className="text-xs font-semibold uppercase tracking-wider">Actions</Label>
                <div className="flex gap-1">
                  <Button type="button" variant="outline" size="sm" onClick={() => addAction('assign_role')} className="rounded-sm text-[10px] h-7">
                    <ShieldCheck size={10} className="mr-1" /> Assign Role
                  </Button>
                  {ruleForm.event_type !== 'joiner' && (
                    <Button type="button" variant="outline" size="sm" onClick={() => addAction('revoke_role')} className="rounded-sm text-[10px] h-7">
                      <Prohibit size={10} className="mr-1" /> Revoke Role
                    </Button>
                  )}
                  {ruleForm.event_type === 'leaver' && (
                    <>
                      <Button type="button" variant="outline" size="sm" onClick={() => addAction('revoke_all_roles')} className="rounded-sm text-[10px] h-7">
                        <XCircle size={10} className="mr-1" /> Revoke All
                      </Button>
                      <Button type="button" variant="outline" size="sm" onClick={() => addAction('disable_identity')} className="rounded-sm text-[10px] h-7">
                        <UserMinus size={10} className="mr-1" /> Disable
                      </Button>
                    </>
                  )}
                  <Button type="button" variant="outline" size="sm" onClick={() => addAction('create_ticket')} className="rounded-sm text-[10px] h-7">
                    <Ticket size={10} className="mr-1" /> Ticket
                  </Button>
                </div>
              </div>
              {ruleForm.actions.length === 0 && <p className="text-xs text-navy-400 text-center py-3 border border-dashed border-slate-200 rounded-sm">No actions configured. Add one above.</p>}
              <div className="space-y-2">
                {ruleForm.actions.map((action, idx) => (
                  <div key={`action-${action.type}-${idx}`} className="flex items-center gap-2 bg-slate-50 border border-slate-200 rounded-sm p-2.5">
                    <Badge className={`text-[10px] shrink-0 ${action.type === 'assign_role' ? 'bg-emerald-100 text-emerald-700' : action.type.includes('revoke') ? 'bg-red-100 text-red-700' : action.type === 'disable_identity' ? 'bg-amber-100 text-amber-700' : 'bg-purple-100 text-purple-700'}`}>
                      {actionTypeLabel(action.type)}
                    </Badge>
                    {(action.type === 'assign_role' || action.type === 'revoke_role') && (
                      <Select value={action.role_id} onValueChange={(v) => updateAction(idx, 'role_id', v)}>
                        <SelectTrigger className="flex-1 h-8 text-xs rounded-sm"><SelectValue placeholder="Select role..." /></SelectTrigger>
                        <SelectContent>
                          {roles.map(r => <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    )}
                    {action.type === 'create_ticket' && (
                      <Input value={action.ticket_description || ''} onChange={(e) => updateAction(idx, 'ticket_description', e.target.value)} className="flex-1 h-8 text-xs rounded-sm" placeholder="Ticket description..." />
                    )}
                    {(action.type === 'revoke_all_roles' || action.type === 'disable_identity') && (
                      <span className="flex-1 text-xs text-navy-500 italic">{action.type === 'revoke_all_roles' ? 'Removes all role assignments' : 'Sets identity to inactive'}</span>
                    )}
                    <Button type="button" variant="ghost" size="sm" onClick={() => removeAction(idx)} className="h-7 w-7 p-0 text-red-400 hover:text-red-600 shrink-0">
                      <Trash size={12} />
                    </Button>
                  </div>
                ))}
              </div>
            </div>

            <div className="flex items-center gap-2">
              <Switch checked={ruleForm.enabled} onCheckedChange={(v) => setRuleForm({...ruleForm, enabled: v})} />
              <Label className="text-sm">Rule enabled</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRuleDialog(false)} className="rounded-sm">Cancel</Button>
            <Button onClick={handleSaveRule} className="bg-orange-500 hover:bg-orange-600 text-white rounded-sm" data-testid="save-rule-btn">
              {editingRule ? 'Update Rule' : 'Create Rule'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* TRIGGER EVENT DIALOG */}
      <Dialog open={showTriggerDialog} onOpenChange={setShowTriggerDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Chivo, sans-serif' }}>
              <div className="flex items-center gap-2"><Play size={18} className="text-orange-500" /> Trigger Lifecycle Event</div>
            </DialogTitle>
            <DialogDescription>Manually trigger a lifecycle event for an identity. Matching rules will execute automatically.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider">Identity *</Label>
              <Popover open={identityPopoverOpen} onOpenChange={setIdentityPopoverOpen}>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="mt-1 w-full justify-between rounded-sm h-10 font-normal" data-testid="trigger-identity-select">
                    {triggerForm.identity_id ? (
                      <span className="flex items-center gap-2">
                        <User size={14} className="text-navy-400" />
                        {identities.find(i => i.id === triggerForm.identity_id)?.display_name || 'Selected'}
                      </span>
                    ) : (
                      <span className="text-navy-400">Search and select an identity...</span>
                    )}
                    <CaretUpDown size={14} className="text-navy-400" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[350px] p-0" align="start">
                  <div className="p-2 border-b border-slate-200">
                    <div className="relative">
                      <MagnifyingGlass size={14} className="absolute left-2 top-1/2 -translate-y-1/2 text-navy-400" />
                      <Input placeholder="Search by name or email..." value={identitySearch} onChange={(e) => setIdentitySearch(e.target.value)} className="pl-7 h-8 text-sm rounded-sm" />
                    </div>
                  </div>
                  <div className="max-h-60 overflow-y-auto">
                    {identities.filter(i => !identitySearch || i.display_name?.toLowerCase().includes(identitySearch.toLowerCase()) || i.email?.toLowerCase().includes(identitySearch.toLowerCase())).slice(0, 50).map(i => (
                      <div key={i.id} className="flex items-center gap-2 px-3 py-2 cursor-pointer hover:bg-slate-100" onClick={() => { setTriggerForm({...triggerForm, identity_id: i.id}); setIdentityPopoverOpen(false); setIdentitySearch(''); }}>
                        <div className={`w-4 h-4 flex items-center justify-center ${triggerForm.identity_id === i.id ? 'text-orange-500' : 'text-transparent'}`}><Check size={14} /></div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-navy-700 truncate">{i.display_name}</p>
                          {i.email && <p className="text-xs text-navy-400 truncate">{i.email}</p>}
                        </div>
                      </div>
                    ))}
                  </div>
                </PopoverContent>
              </Popover>
            </div>
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider">Event Type *</Label>
              <Select value={triggerForm.event_type} onValueChange={(v) => setTriggerForm({...triggerForm, event_type: v})}>
                <SelectTrigger className="mt-1 rounded-sm"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="joiner"><div className="flex items-center gap-2"><UserPlus size={14} className="text-emerald-500" /> Joiner</div></SelectItem>
                  <SelectItem value="mover"><div className="flex items-center gap-2"><ArrowsLeftRight size={14} className="text-blue-500" /> Mover</div></SelectItem>
                  <SelectItem value="leaver"><div className="flex items-center gap-2"><UserMinus size={14} className="text-red-500" /> Leaver</div></SelectItem>
                </SelectContent>
              </Select>
            </div>
            {triggerForm.event_type === 'mover' && (
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs font-semibold uppercase tracking-wider">Old Department</Label>
                  <Input value={triggerForm.details.old_department || ''} onChange={(e) => setTriggerForm({...triggerForm, details: {...triggerForm.details, old_department: e.target.value}})} className="mt-1 rounded-sm text-sm" />
                </div>
                <div>
                  <Label className="text-xs font-semibold uppercase tracking-wider">New Department</Label>
                  <Input value={triggerForm.details.new_department || ''} onChange={(e) => setTriggerForm({...triggerForm, details: {...triggerForm.details, new_department: e.target.value}})} className="mt-1 rounded-sm text-sm" />
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowTriggerDialog(false)} className="rounded-sm">Cancel</Button>
            <Button onClick={handleTrigger} className="bg-orange-500 hover:bg-orange-600 text-white rounded-sm" data-testid="confirm-trigger-btn">
              <Play size={14} className="mr-1.5" /> Trigger Event
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
